import { createFinishStepContent } from '../FinishStepContent';
import { closeSidebarPanel, openCampaignsSubmenu } from '../sidebarHelpers';

const firstOneOffEmail = (baseUrl, navigate, user, everyoneSegment) => {
  return {
    id: 'firstOneOffEmail',
    title: 'Create a one off email campaign and send a test email ',
    steps: [
      {
        selector: '[data-tour="campaignLink"]',
        content: <p>Hover over or click on the "Campaigns" icon in the sidebar to open the campaigns menu.</p>,
      },
      {
        actionBefore: () => openCampaignsSubmenu('[data-tour="onOffLink"]'),
        selector: '[data-tour="onOffLink"]',
        content: <p>Click on "One Off" to view one-off campaigns.</p>,
      },
      {
        actionBefore: () => {
          closeSidebarPanel();
          navigate(`${baseUrl}/campaigns?mode=oneOff`, true);
        },
        selector: '[data-tour="newElementBtn"]',
        content: <p>Click on the "New Campaign" button to create a new campaign.</p>,
      },
      {
        actionBefore: () =>
          navigate(`${baseUrl}/campaigns/new?mode=oneOff&preselectSegmentId=${everyoneSegment.id}`, true),
        selector: '[data-tour="addNameToCampaign"]',
        content: <p>The campaign name is internal and you can make it as detailed as you want.</p>,
        stepInteraction: true,
      },
      {
        selector: '[data-tour="newEmail"]',
        content: <p>Click on the "New email" button to create a blank email and attach it to the campaign.</p>,
      },
      {
        selector: '[data-tour="emailFormInnerFields"]',
        content: <p>Your customers will see the Subject line, Preview text, and Sender's name.</p>,
        actionBefore: () => document.querySelector('[data-tour="newEmail"]')?.click(),
      },
      {
        selector: '[data-tour="emailFormInnerVisualContent"]',
        stepInteraction: true,
        content: (
          <p>
            We have a visual editor that allows you to create your emails by dragging and dropping elements right into
            your message.
          </p>
        ),
      },
      {
        selector: '[data-tour="testEmailBtn"]',
        content: <p>By clicking here you can test the email you just created.</p>,
      },
      {
        selector: '[data-tour="testEmailModal"]',
        stepInteraction: true,
        content: <p>Here you can select which email address you want your test email to be sent to.</p>,
        actionBefore: () => document.querySelector('[data-tour="testEmailBtn"]')?.click(),
      },
      {
        selector: '[data-tour="testEmailModalSendBtn"]',
        content: <p>Click "Send".</p>,
      },
      {
        selector: '[data-tour="emailFormInnerVisualContent"]',
        stepInteraction: true,
        content: <p>Now you can check your email and resume making changes to your campaign.</p>,
        actionBefore: () => document.querySelector('[data-tour="testEmailModalSendBtn"]')?.click(),
      },
      {
        selector: '[data-tour="emailFormNextButton"]',
        stepInteraction: false,
        content: <p>Once you are done, click on the Next button.</p>,
      },
      {
        selector: '[data-tour="emailFormInnerBehaviorFields"]',
        stepInteraction: true,
        content: <p>In the "Behavior" tab, you can control advanced email behavior.</p>,
        actionBefore: () => document.querySelector('[data-tour="emailFormNextButton"]')?.click(),
      },
      {
        selector: '[data-tour="emailFormSaveButton"]',
        stepInteraction: false,
        content: <p>Finally, save the email by clicking "Save".</p>,
      },
      {
        selector: '[data-tour="nextButtonCampaign"]',
        content: (
          <p>
            Now that we have set up the email, we can see a preview of its content. To configure the campaign, click on
            the "Next" button.
          </p>
        ),
        actionBefore: () => document.querySelector('[data-tour="emailFormSaveButton"]')?.click(),
      },
      {
        selector: '[data-tour="audienceScope"]',
        content: (
          <>
            <p>Select an audience for your campaign - these are all the users who will receive your email.</p>
            <p>
              You can send the campaign to all users or a specific group of users. Please note the campaigns are two
              types - Marketing and Transactional.
            </p>
            <p>
              Transactional is only used when you need to make an announcement about changes in Terms of service or
              Delivery options.
            </p>
            <p>All other emails are considered Marketing messages and should be sent as such.</p>
          </>
        ),
        stepInteraction: true,
        actionBefore: () => document.querySelector('[data-tour="camapaignAudienceTab"]')?.click(),
      },
      {
        actionBefore: () => {
          document.querySelector('[data-tour="camapaignAudienceTab"]')?.click();
          navigate(`${baseUrl}/campaigns/new?mode=oneOff&editor=unlayer`, true);
        },
        selector: '[data-tour="audienceSegment"]',
        content: (
          <>
            <p>In this section, you can select what group of users you will address with your campaign.</p>
            <p>
              You can select from a number of predefined segments or you can create your own in the Segment section.
            </p>
            <p>When you're sending your weekly newsletter you should select the segment called "Everyone".</p>
          </>
        ),
        stepInteraction: true,
      },
      {
        selector: '[data-tour="nextButtonCampaign"]',
        content: <p>Click on the "Next" button.</p>,
      },
      {
        selector: '[data-tour="delayedStart"]',
        content: (
          <p>
            Here you can select a future start date for your campaign. You can prepare your campaigns in advance and
            they will start at the time you set in the future.
          </p>
        ),
        stepInteraction: true,
        actionBefore: () => document.querySelector('[data-tour="camapaignLaunchConfigurationTab"]')?.click(),
      },
      {
        selector: '[data-tour="campaignGoalSettings"]',
        content: (
          <p>
            Here you can configure what the goal of the campaign is. You will then be able to see the number of times
            the campaign achieved its goal, alongside the associated monetary value.
          </p>
        ),
        stepInteraction: true,
        actionBefore: () => document.querySelector('[data-tour="camapaignGoalTab"]')?.click(),
      },
      {
        selector: '[data-tour="saveBtn"]',
        content: <p>Click on the "Save & Close" button.</p>,
      },
      {
        selector: '[data-tour="campaignCard"]',
        content: (
          <>
            <p>When your campaign is ready to be sent you need to click the Launch button.</p>
            <p>
              Please note that campaigns that include personalized product selection for each user will take longer to
              be sent than campaigns that only include a visual.
            </p>
            <p>On average 10,000 emails with only a visual are sent in about 15 minutes.</p>
          </>
        ),
        stepInteraction: true,
        actionBefore: () => document.querySelector('[data-tour="saveBtn"]')?.click(),
      },
      {
        selector: '[data-tour="campaignStatus"]',
        content: (
          <>
            <p>
              After your campaign is launched the status will be running and it will change to Finished after all the
              emails are sent.
            </p>
            <p>All statistics for each campaign will be updated a few hours after sending.</p>
          </>
        ),
        stepInteraction: true,
      },
      {
        actionBefore: () => navigate(`${baseUrl}/guide`, true),
        selector: '[data-tour="userGuideHeading"]',
        content: createFinishStepContent(),
      },
    ],
    isComplete: !!user.userActions.find((a) => a.action === 'taskCompleted' && a.context.task === 'firstOneOffEmail'),
  };
};

export default firstOneOffEmail;
